import { DATE_FORMAT } from "@/constants";
import type { TermUomType } from "@/gql/graphql";
import { ContractStatusType } from "@/gql/graphql";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import type { TFunction } from "i18next";
import { format } from "./format";

dayjs.extend(duration);

export { format } from "./format";

// GraphQL list inputs accept a single value or an array (OneOrMany coercion).
// Normalize to an array so callers can use array operations uniformly.
export const toArrayInput = <T>(value: T | T[] | null | undefined): T[] => {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
};

export enum StartDateType {
  DATE = "DATE",
  TARGET_DATE = "TARGET_DATE",
  UPON_ACTIVATION = "UPON_ACTIVATION",
}

export const getStartDateType = (
  status: ContractStatusType,
  date?: string | null
) => {
  if (date) {
    if (status === ContractStatusType.Activated) {
      return StartDateType.DATE;
    }
    return StartDateType.TARGET_DATE;
  }
  if (status !== ContractStatusType.Activated) {
    return StartDateType.UPON_ACTIVATION;
  }
};

export const daysLeft = (date: string): number =>
  dayjs.duration(dayjs(date).diff(dayjs())).asDays();

// Returns the number of calendar days between two dates (inclusive).
// Uses format.formatDate to normalize to date-only, then parses and diffs.
export const diffInDays = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number => {
  const startStr = format.formatDate(startDate);
  const endStr = format.formatDate(endDate);
  if (!startStr || !endStr) return 0;
  const start = dayjs(startStr, DATE_FORMAT.DATE);
  const end = dayjs(endStr, DATE_FORMAT.DATE);
  return end.diff(start, "day") + 1;
};

// Using css vars in MUI createTheme https://stackoverflow.com/a/70275528
export const cssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function optionalEnum<
  E extends Record<string, string>,
  K extends string = "Empty",
>(e: E, emptyKey: K = "Empty" as K): E & Record<K, ""> {
  return { ...e, [emptyKey]: "" } as E & Record<K, "">;
}

export function separateValues(input: string): string[] {
  return input
    .split(/[ ,;]+/)
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

/**
 * Returns the trimmed value when it has at least one non-whitespace
 * character, otherwise `undefined`. Useful for chaining with nullish
 * coalescing when empty / whitespace-only strings should also be treated
 * as "absent" — e.g. when an API returns `""` for an unknown name and a
 * plain `name ?? fallback` would render a blank label:
 *
 *   nonBlankString(organization.name) ??
 *   nonBlankString(organization.website) ??
 *   t("unknown");
 */
export const nonBlankString = (
  value: string | null | undefined
): string | undefined => {
  const trimmed = value?.trim();
  return trimmed?.length ? trimmed : undefined;
};

export const isBundleSkuType = (name: string | undefined | null): boolean =>
  (name ?? "") === "BUNDLE";

export const isPackageSkuType = (name: string | undefined | null): boolean =>
  (name ?? "") === "PACKAGE";

export const shouldShowProductIdBelowTitle = (
  name: string | undefined | null
): boolean => {
  if (name == null || name === "") return true;
  return name === "STANDARD";
};

export const isBundleOrPackageSkuType = (
  name: string | undefined | null
): boolean => (name ?? "") === "BUNDLE" || (name ?? "") === "PACKAGE";

export const getSkuTypeDisplayName = (
  t: TFunction,
  name: string | undefined | null,
  fallbackKey = "common.skuType.subscription"
): string => {
  if (!name) {
    return fallbackKey === ""
      ? ""
      : t(fallbackKey, { defaultValue: fallbackKey });
  }
  let translationKey: string | null = null;
  if (name === "STANDARD") translationKey = "common.skuType.standard";
  else if (name === "BUNDLE") translationKey = "common.skuType.bundle";
  else if (name === "PACKAGE") translationKey = "common.skuType.package";
  return translationKey !== null
    ? t(translationKey, { defaultValue: translationKey })
    : name;
};

export const getTermLength = (
  t: TFunction,
  startDate: string,
  endDate: string,
  termQuantity: number,
  termUOM: TermUomType
): string => {
  if (termQuantity <= 0 && startDate && endDate) {
    const normalizedStartDate = dayjs(startDate).startOf("day");
    const normalizedEndDate = dayjs(endDate).startOf("day");
    const termLength = normalizedEndDate.diff(normalizedStartDate, "day");
    return t(`contract_summary.terms_card.in_days`, {
      count: termLength,
    });
  }
  return t(`contract_summary.terms_card.${termUOM.toLowerCase()}`, {
    count: termQuantity,
  });
};
